/**
 * Channel screen — Screen 2 of the two-screen client.
 *
 * Renders the in-channel UI (channel name, header cog that toggles
 * an audio-settings panel, talker list, scrollable member list, big
 * PTT button) and wires the [`NettaccoTransport`] events to the UI
 * components and to the audio pipeline.
 *
 * Audio lifecycle (the single most load-bearing thing this screen does):
 *
 *  - **Playback** opens on mount and runs for the whole session. Where
 *    the browser supports `AudioContext.setSinkId` we honour the
 *    user's chosen output device; on Safari/iOS the OS picks the
 *    route via Control Center.
 *
 *  - **Capture** opens on PTT-press and is **destroyed on release.**
 *    Holding the mic open puts the OS into voice-call routing:
 *    Bluetooth flips to HFP (narrow-band hands-free, takes over a
 *    car stereo for as long as the mic is held), iOS routes output
 *    to the earpiece, Android does similar. Opening only for the
 *    duration of a press means the OS restores media routing
 *    (A2DP, loudspeaker) the moment the user lets go.
 *
 *  - **`navigator.audioSession.type`** is pinned to `"playback"` on
 *    mount and bumped to `"play-and-record"` while capture is open.
 *    iOS Safari (17+) reads this hint to decide its routing mode;
 *    explicit values keep listening sessions firmly in media mode
 *    even after a recent press.
 *
 * First-press latency: `getUserMedia` takes a few hundred ms (plus an
 * interactive permission prompt the very first time on a fresh
 * origin). The floor-grant round-trip overlaps the startup; if the
 * grant lands while the mic is still initialising, the resolve
 * handler opens the transmit gate as soon as the mic is ready.
 */

import type { HelloOk } from "./protocol";
import type {
    ConnectionState,
    NettaccoTransport,
    TransportListeners,
} from "./transport";
import { startCapture, type CaptureHandle } from "./audio/capture";
import { startPlayback, type PlaybackHandle } from "./audio/playback";
import { createPttButton, type PttButtonHandle, type PttButtonState } from "./ui/ptt-button";
import { createTalkerList, type TalkerListHandle } from "./ui/talker-list";
import { createMemberList, type MemberListHandle } from "./ui/member-list";
import {
    createAudioSettings,
    loadAudioSettings,
    type AudioSettings,
    type AudioSettingsHandle,
} from "./ui/audio-settings";

/** Constructor options for [`renderChannel`]. */
export interface ChannelOptions {
    /** Container the channel UI is appended into. */
    container: HTMLElement;
    /** Already-connected transport. The channel screen does not connect itself. */
    transport: NettaccoTransport;
    /** Result of the handshake; supplies channel name and the initial talker snapshot. */
    helloOk: HelloOk;
    /** Called when the user hits the disconnect button. */
    onDisconnect: () => void;
}

/** Handle returned by [`renderChannel`] for teardown. */
export interface ChannelHandle {
    destroy(): void;
}

/** Subset of `AudioSession.type` values we use. */
type AudioSessionType = "auto" | "playback" | "play-and-record";

/**
 * iOS Safari (17+) exposes `navigator.audioSession.type`; setting it
 * tells the OS whether the page expects to record. We pin it to
 * `"playback"` while idle so listening uses the loudspeaker / A2DP
 * route, and bump to `"play-and-record"` only for the duration of a
 * PTT press. No-op on every browser that lacks the API.
 */
function setAudioSessionType(type: AudioSessionType): void {
    const nav = navigator as Navigator & {
        audioSession?: { type: AudioSessionType };
    };
    if (nav.audioSession === undefined) return;
    try {
        nav.audioSession.type = type;
    } catch {
        // Some browsers may reject specific values; ignore.
    }
}

/**
 * Render the channel screen and attach all event handlers.
 *
 * The transport is *not* owned by the channel screen — disconnection is
 * delegated upward via `onDisconnect` so the app entry can decide what
 * to do next (return to the lobby with the URL prefilled, etc.).
 */
export function renderChannel(opts: ChannelOptions): ChannelHandle {
    const root = document.createElement("section");
    root.className = "channel";
    opts.container.appendChild(root);

    // Pin the iOS audio session to playback mode for the listening
    // case; capture-open will bump it to play-and-record temporarily.
    setAudioSessionType("playback");

    // ---- header --------------------------------------------------------
    const header = document.createElement("header");
    header.className = "channel-header";

    const titleGroup = document.createElement("div");
    const title = document.createElement("div");
    title.className = "channel-name";
    title.textContent = opts.helloOk.channel;
    const connState = document.createElement("div");
    connState.className = "conn-state";
    connState.textContent = "Connected";
    titleGroup.appendChild(title);
    titleGroup.appendChild(connState);

    // Settings cog — toggles the audio-settings panel below the header.
    // Placed before the disconnect button so the destructive action
    // stays at the screen edge, away from a stray thumb.
    const settingsBtn = document.createElement("button");
    settingsBtn.type = "button";
    settingsBtn.className = "settings";
    settingsBtn.setAttribute("aria-label", "Audio settings");
    settingsBtn.setAttribute("aria-expanded", "false");
    settingsBtn.textContent = "⚙"; // U+2699 GEAR

    const disconnectBtn = document.createElement("button");
    disconnectBtn.type = "button";
    disconnectBtn.className = "disconnect";
    disconnectBtn.textContent = "Disconnect";
    const onDisconnectClick = (): void => opts.onDisconnect();
    disconnectBtn.addEventListener("click", onDisconnectClick);

    const headerActions = document.createElement("div");
    headerActions.className = "header-actions";
    headerActions.appendChild(settingsBtn);
    headerActions.appendChild(disconnectBtn);

    header.appendChild(titleGroup);
    header.appendChild(headerActions);
    root.appendChild(header);

    // Audio-settings panel host — sits directly below the header and is
    // hidden by default. The cog click toggles it. Placed here (rather
    // than just above the PTT button as before) so the visual model
    // matches the toggle: cog → drop-down → close.
    const audioSettingsHost = document.createElement("div");
    audioSettingsHost.className = "audio-settings-host";
    audioSettingsHost.hidden = true;
    root.appendChild(audioSettingsHost);

    const onSettingsClick = (): void => {
        const open = audioSettingsHost.hidden;
        audioSettingsHost.hidden = !open;
        settingsBtn.setAttribute("aria-expanded", open ? "true" : "false");
        settingsBtn.classList.toggle("active", open);
    };
    settingsBtn.addEventListener("click", onSettingsClick);

    // ---- talker list (pinned: who is talking right now) ---------------
    const talkerListHost = document.createElement("div");
    root.appendChild(talkerListHost);
    const talkerList: TalkerListHandle = createTalkerList({
        container: talkerListHost,
        initial: opts.helloOk.current_talkers,
    });

    // ---- member list (scrollable: full channel roster) ----------------
    //
    // Talker rows are duplicated here intentionally: the pinned talker
    // list answers "who is speaking?" and the member list answers "who
    // is here?". A talker who scrolls out of view in the member list
    // remains visible in the talker list at the top of the screen.
    const memberListHost = document.createElement("div");
    root.appendChild(memberListHost);
    /** Live set of member ids currently holding a floor slot. */
    const talkerIds = new Set<string>(
        opts.helloOk.current_talkers.map((t) => t.connection_id),
    );
    const memberList: MemberListHandle = createMemberList({
        container: memberListHost,
        initial: opts.helloOk.current_members ?? [],
        selfConnectionId: opts.helloOk.connection_id,
    });
    memberList.setTalkers(talkerIds);

    // ---- audio settings state ----------------------------------------
    //
    // The settings panel itself is hosted directly below the header
    // (see `audioSettingsHost` above) and toggled by the cog button.
    // We track the live values here so the capture closure can read
    // mic gain off the latest state without re-opening the mic, and
    // so playback can be told about device / volume changes.
    let audioSettingsState: AudioSettings = loadAudioSettings();

    // ---- PTT button ----------------------------------------------------
    const pttHost = document.createElement("div");
    pttHost.className = "ptt-area";
    root.appendChild(pttHost);

    /** Pending: user is holding the button, waiting on the server's grant/deny. */
    let awaitingFloorReply = false;
    /** Whether the server has currently granted us the floor. */
    let weHoldFloor = false;

    // ---- audio pipeline ------------------------------------------------

    let capture: CaptureHandle | null = null;
    /** True while a `startCapture` promise is in flight. */
    let captureInFlight = false;
    /**
     * Mirrors the PTT-pressed state. If `false` by the time an
     * in-flight `startCapture` resolves, the new handle is destroyed
     * immediately — the user has already let go.
     */
    let wantCapture = false;
    let playback: PlaybackHandle | null = null;
    let teardown = false;

    void startPlayback({
        deviceId: audioSettingsState.speakerDeviceId,
        volume: audioSettingsState.speakerVolume ?? 1.0,
        onError: (e) => {
            connState.textContent = `Audio playback error: ${e.message}`;
        },
    })
        .then((handle) => {
            if (teardown) {
                handle.destroy();
                return;
            }
            playback = handle;
        })
        .catch((e: Error) => {
            connState.textContent = `Audio playback error: ${e.message}`;
        });

    /**
     * Open the microphone for the current PTT press. Idempotent —
     * calling while a previous open is still in flight is a no-op.
     *
     * Frames flow into [`opts.transport.sendAudio`] only after the
     * resulting handle has had `startTransmitting()` called on it
     * (gated on `floor_granted`). Until then the worklet still pumps
     * frames cross-thread but the main-thread bridge drops them.
     */
    const openCapture = (): void => {
        if (capture !== null || captureInFlight) return;
        captureInFlight = true;
        setAudioSessionType("play-and-record");
        void startCapture({
            deviceId: audioSettingsState.micDeviceId,
            getGain: () => audioSettingsState.micGain ?? 1.0,
            onFrame: (payload, sequence, timestampMs) => {
                opts.transport.sendAudio(payload, sequence, timestampMs);
            },
            onLevel: (rms) => {
                ptt.setLevel(rms);
            },
            onError: (e) => {
                connState.textContent = `Audio capture error: ${e.message}`;
            },
        })
            .then((handle) => {
                captureInFlight = false;
                if (teardown || !wantCapture) {
                    // User let go (or the screen tore down) before the
                    // mic finished initialising. Discard the handle and
                    // restore the playback-only audio session.
                    handle.destroy();
                    setAudioSessionType("playback");
                    return;
                }
                capture = handle;
                if (weHoldFloor) handle.startTransmitting();
                // Refresh the audio-settings dropdowns now that the
                // first getUserMedia grant has populated device labels.
                void audioSettings.refresh();
            })
            .catch((e: Error) => {
                captureInFlight = false;
                wantCapture = false;
                setAudioSessionType("playback");
                connState.textContent = `Microphone unavailable: ${e.message}`;
            });
    };

    /**
     * Close the microphone and release the OS audio route. Idempotent.
     *
     * This is what causes Bluetooth headsets to flip back from HFP to
     * A2DP, iPhones to route subsequent audio to the loudspeaker
     * rather than the earpiece, and the level meter to drop to zero.
     */
    const closeCapture = (): void => {
        wantCapture = false;
        if (capture !== null) {
            capture.destroy();
            capture = null;
        }
        ptt.setLevel(0);
        // If a startCapture promise is still in flight the resolve /
        // catch handler will reset the session type — don't pre-empt it
        // here, since it would race against an ongoing `play-and-record`
        // bump from `openCapture`.
        if (!captureInFlight) {
            setAudioSessionType("playback");
        }
    };

    /**
     * Reset the visual state to whatever the *current* button + floor
     * combination implies. Called after every relevant event.
     */
    const updateButtonVisual = (): void => {
        let state: PttButtonState;
        if (weHoldFloor) {
            state = "granted";
        } else if (awaitingFloorReply) {
            state = "pressing";
        } else {
            state = "idle";
        }
        ptt.setState(state);
    };

    const ptt: PttButtonHandle = createPttButton({
        container: pttHost,
        onPress: () => {
            awaitingFloorReply = true;
            wantCapture = true;
            opts.transport.requestFloor();
            // Open the mic in parallel with the floor request. Each
            // press triggers a fresh getUserMedia; the OS flips to
            // voice-call routing for the duration and back to media
            // when we destroy on release.
            openCapture();
            updateButtonVisual();
        },
        onRelease: () => {
            // Always release on the wire, even if we never got a grant —
            // a denied request followed by release is harmless and keeps
            // the local + server state aligned.
            awaitingFloorReply = false;
            weHoldFloor = false;
            closeCapture();
            opts.transport.releaseFloor();
            updateButtonVisual();
        },
    });

    // The audio-settings panel is constructed after `ptt` so that
    // forward references inside the `onChange` callback resolve
    // cleanly. Speaker device change applies immediately via
    // `setSinkId`; speaker volume applies immediately via the master
    // GainNode; mic device change takes effect on the next press
    // (re-opening mid-press would whip the OS through a route change
    // while the user is mid-sentence); mic gain is read from
    // `audioSettingsState` per-frame by the capture pipeline so it
    // takes effect on the next worklet frame, mid-press if needed.
    //
    // Slider drags fire `onChange` per input tick, so we guard the
    // expensive `setSinkId` against no-op re-application.
    const audioSettings: AudioSettingsHandle = createAudioSettings({
        container: audioSettingsHost,
        initial: audioSettingsState,
        onChange: (sel) => {
            const speakerChanged = sel.speakerDeviceId !== audioSettingsState.speakerDeviceId;
            const volumeChanged =
                (sel.speakerVolume ?? 1.0) !== (audioSettingsState.speakerVolume ?? 1.0);
            audioSettingsState = sel;
            if (speakerChanged) {
                void playback?.setSinkId(sel.speakerDeviceId);
            }
            if (volumeChanged) {
                playback?.setVolume(sel.speakerVolume ?? 1.0);
            }
        },
    });

    // ---- transport listeners ------------------------------------------
    //
    // The transport was given placeholder listeners at construction (or
    // none); install the channel-screen listeners here. We replace any
    // previous listeners by mutating the transport's listener bag — the
    // app entry guarantees the channel screen is the sole owner from
    // hello_ok onward.

    const previousListeners: TransportListeners = opts.transport.setListeners({
        onStateChange: (state, reason) => {
            connState.textContent = describeState(state, reason);
            if (state === "disconnected") {
                opts.onDisconnect();
            }
        },
        onFloorTaken: (talker) => {
            talkerList.add(talker);
            talkerIds.add(talker.connection_id);
            memberList.setTalkers(talkerIds);
        },
        onFloorReleased: (id) => {
            talkerList.remove(id);
            talkerIds.delete(id);
            memberList.setTalkers(talkerIds);
            // Free the per-sender decoder so the next time this id
            // appears we start with fresh decoder state. Idempotent
            // even if no decoder was ever created for this id.
            playback?.removeSender(id);
        },
        onMemberJoined: (member) => {
            memberList.add(member);
        },
        onMemberLeft: (id) => {
            // floor_released arrives separately when the leaver was a
            // talker, so no need to touch talkerIds here.
            memberList.remove(id);
        },
        onFloorGranted: () => {
            awaitingFloorReply = false;
            weHoldFloor = true;
            // Open the transmit gate now if capture is ready; if it is
            // still initialising the resolve handler in `openCapture`
            // checks `weHoldFloor` itself and opens the gate then.
            capture?.startTransmitting();
            updateButtonVisual();
        },
        onFloorDenied: () => {
            awaitingFloorReply = false;
            weHoldFloor = false;
            // No grant means no audio will ever flow this press; tear
            // the mic down right away rather than holding the OS in
            // voice-routing mode while the user keeps a finger on the
            // button.
            closeCapture();
            ptt.setState("denied");
            // Brief visual flash; hold-to-talk users will let go and the
            // next render pass picks up the idle state via release.
            window.setTimeout(() => {
                if (!awaitingFloorReply && !weHoldFloor) {
                    ptt.setState("idle");
                }
            }, 600);
        },
        onAudio: (frame) => {
            playback?.pushFrame(frame.sender, frame.opus_payload);
        },
        onReconnected: (ok) => {
            // The server gave us a fresh `connection_id` and updated
            // snapshots for both the talker and member lists. Refresh
            // both, drop any per-sender decoder state from before the
            // gap, and reset local floor flags — we are definitely not
            // holding the floor on the new connection until we
            // re-request it.
            talkerList.setAll(ok.current_talkers);
            memberList.setAll(ok.current_members ?? []);
            memberList.updateSelf(ok.connection_id);
            talkerIds.clear();
            for (const t of ok.current_talkers) talkerIds.add(t.connection_id);
            memberList.setTalkers(talkerIds);
            playback?.clear();
            awaitingFloorReply = false;
            weHoldFloor = false;
            closeCapture();
            updateButtonVisual();
        },
        onServerError: (code, message) => {
            connState.textContent = `Error: ${code} — ${message}`;
        },
        onTransportError: (message) => {
            connState.textContent = `Connection error: ${message}`;
        },
    });

    return {
        destroy(): void {
            teardown = true;
            disconnectBtn.removeEventListener("click", onDisconnectClick);
            settingsBtn.removeEventListener("click", onSettingsClick);
            closeCapture();
            audioSettings.destroy();
            ptt.destroy();
            talkerList.destroy();
            memberList.destroy();
            playback?.destroy();
            setAudioSessionType("auto");
            // Restore whatever listeners were on the transport before us
            // — the app entry typically tears down the transport itself
            // in response to onDisconnect, but this keeps the screen
            // self-contained and reusable.
            opts.transport.setListeners(previousListeners);
            root.remove();
        },
    };
}

/**
 * Render a connection state into the small status strip under the
 * channel name. Kept terse — the UI uses it as a status hint, not a log.
 */
function describeState(state: ConnectionState, reason?: string): string {
    switch (state) {
        case "idle":
            return "";
        case "connecting":
            return "Connecting…";
        case "handshaking":
            return "Authenticating…";
        case "connected":
            return "Connected";
        case "reconnecting":
            return reason !== undefined ? `Reconnecting… (${reason})` : "Reconnecting…";
        case "disconnected":
            return reason !== undefined ? `Disconnected (${reason})` : "Disconnected";
    }
}
